import type { SupportedLocale } from '~/utils/localeController'

export type FontAssetResult = {
  preload: string[]
  css: string
}

// Map locale to required subsets
const LOCALE_SUBSETS: Record<SupportedLocale, ('latin' | 'latin-ext' | 'cyrillic')[]> = {
  en: ['latin'],
  da: ['latin', 'latin-ext'],
  ru: ['latin', 'cyrillic'],
  kk: ['latin', 'cyrillic'],
  // CJK languages do not download Google Latin fonts by default (will fallback to system CJK)
  zh: [],
  ja: [],
  ko: [],
}

// Mapping of subset names to their unicode ranges (Google Fonts specs)
export const SUBSET_UNICODE_RANGES = {
  'latin': 'unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;',
  'latin-ext': 'unicode-range: U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF;',
  'cyrillic': 'unicode-range: U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116;',
}

// CJK fallback font stack for high performance and native feeling
export const CJK_FONT_FAMILY_STACK = '"PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "Noto Sans CJK SC", "Meiryo", "Malgun Gothic", "Apple SD Gothic Neo", sans-serif'

// Preset weights for Google Fonts we support
const FONT_FAMILY_WEIGHTS: Record<string, number[]> = {
  'roboto': [400, 500, 700],
  'open-sans': [400, 500, 700],
  'lato': [400, 700],
  'montserrat': [400, 500, 700],
  'poppins': [400, 500, 700],
  'nunito-sans': [400, 600, 700],
  'inter': [400, 500, 700],
  'jetbrains-mono': [400, 500],
}

// Get clean label of family (e.g. "Nunito Sans")
const FAMILY_LABEL_MAP: Record<string, string> = {
  'roboto': 'Roboto',
  'open-sans': 'Open Sans',
  'lato': 'Lato',
  'montserrat': 'Montserrat',
  'poppins': 'Poppins',
  'nunito-sans': 'Nunito Sans',
  'inter': 'Inter',
  'jetbrains-mono': 'JetBrains Mono',
}

export function resolveFontAssets(familyKey: string, locale: SupportedLocale): FontAssetResult {
  const normalizedKey = String(familyKey || '').trim().toLowerCase()
  const familyName = FAMILY_LABEL_MAP[normalizedKey] || FAMILY_LABEL_MAP['roboto']
  const finalKey = FAMILY_LABEL_MAP[normalizedKey] ? normalizedKey : 'roboto'
  const weights = FONT_FAMILY_WEIGHTS[finalKey] || [400, 700]
  
  const subsets = LOCALE_SUBSETS[locale] || ['latin']
  const preload: string[] = []
  let css = ''

  // If CJK locale and no special subsets wanted, just return empty CSS/preload
  // It will fall back nicely to the CJK system font stack
  if (subsets.length === 0) {
    return { preload, css }
  }

  // Preload the core 'latin' subset for the primary regular font weight (400) to cut FCP waterfall
  if (subsets.includes('latin')) {
    preload.push(`/fonts/google/${finalKey}/${finalKey}-400.latin.woff2`)
  }

  // Generate dynamic @font-face rules for needed subsets and weights
  for (const weight of weights) {
    for (const subset of subsets) {
      const fontUrl = `/fonts/google/${finalKey}/${finalKey}-${weight}.${subset}.woff2`
      const rangeRule = SUBSET_UNICODE_RANGES[subset] || ''
      
      css += `@font-face {
  font-family: '${familyName}';
  src: url('${fontUrl}') format('woff2');
  font-style: normal;
  font-weight: ${weight};
  font-display: swap;
  ${rangeRule}
}
`
      // Also write lowercased alias as the CMS references fontFamilies in lowercase occasionally
      if (familyName.toLowerCase() !== familyName) {
        css += `@font-face {
  font-family: '${familyName.toLowerCase()}';
  src: url('${fontUrl}') format('woff2');
  font-style: normal;
  font-weight: ${weight};
  font-display: swap;
  ${rangeRule}
}
`
      }
    }
  }

  return { preload, css }
}
